import { useEffect, useRef, useState, useCallback } from "react";
import * as Tone from "tone";
import { Midi } from "@tonejs/midi";
import { getPiano, kickAudio, onAudioStop, stopAllAudio } from "@/audio/piano";

/**
 * MIDI playback for a lesson exercise.
 *
 *  • Loads a `.mid` file (URL or data URL) on demand
 *  • Renders play / pause / stop / loop + a tempo slider expressed as % of original
 *  • Plays through the shared Salamander grand piano sampler
 *  • Emits per-note callbacks (pitch class only, never an octave-suffixed name)
 *    so a parent component can highlight a PianoDiagram
 *
 * Deliberately UI-light. No note-name display, no MIDI numbers visible.
 */

interface MidiPlayerProps {
  /** URL or data URL of the .mid file. */
  src: string;
  /** Optional label shown above the player ("Bass groove", "Full arrangement", etc.). */
  label?: string;
  /** Forced original BPM. If omitted, falls back to the file's first tempo event. */
  originalBpm?: number;
  /** Called whenever a note ON event fires. pc = 0..11 pitch class. */
  onNote?: (pc: number, midi: number) => void;
  /** Called when playback ends naturally (not via stop). */
  onEnd?: () => void;
}

interface ParsedMidi {
  notes: { pc: number; midi: number; pitchName: string; time: number; duration: number; velocity: number }[];
  /** Total duration in beats so we can show progress with a single math */
  durationSec: number;
  fileBpm: number;
}

const PITCH_LETTERS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
function midiToPitchName(midi: number): string {
  const pc = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  return `${PITCH_LETTERS[pc]}${oct}`;
}

async function loadMidi(src: string, fallbackBpm: number | undefined): Promise<ParsedMidi> {
  const midi = await Midi.fromUrl(src);
  const fileBpm = midi.header.tempos[0]?.bpm ?? fallbackBpm ?? 100;
  const notes = midi.tracks
    .flatMap((t) => t.notes.map((n) => ({
      pc: ((n.midi % 12) + 12) % 12,
      midi: n.midi,
      pitchName: midiToPitchName(n.midi),
      time: n.time,
      duration: n.duration,
      velocity: n.velocity,
    })))
    .sort((a, b) => a.time - b.time);
  const durationSec = notes.reduce((max, n) => Math.max(max, n.time + n.duration), 0);
  return { notes, durationSec, fileBpm };
}

export function MidiPlayer({ src, label, originalBpm, onNote, onEnd }: MidiPlayerProps) {
  const [parsed, setParsed] = useState<ParsedMidi | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(true);
  const [speedPct, setSpeedPct] = useState(100);
  const [progress, setProgress] = useState(0); // 0..1
  const scheduledIdsRef = useRef<number[]>([]);
  const startedAtRef = useRef<number | null>(null); // Tone time when current pass started
  const pausedAtRef = useRef<number | null>(null);  // elapsed seconds when paused
  const tickRafRef = useRef<number | null>(null);

  // Load whenever src changes
  useEffect(() => {
    setError(null);
    setParsed(null);
    if (!src) return;
    setLoading(true);
    loadMidi(src, originalBpm)
      .then((p) => setParsed(p))
      .catch((e) => setError(e?.message ?? "Failed to load MIDI"))
      .finally(() => setLoading(false));
  }, [src, originalBpm]);

  const parsedRef = useRef<ParsedMidi | null>(null);
  parsedRef.current = parsed;
  const loopRef = useRef(true);
  loopRef.current = loop;

  const speed = speedPct / 100;
  const fileBpm = parsed?.fileBpm ?? 100;
  const effectiveBpm = Math.round(fileBpm * speed);
  const realDurationSec = parsed ? parsed.durationSec / speed : 0;

  // Keep Tone.Transport.bpm synced with our slider while playing.
  // Guarded by `playing`: an idle player's slider (or a re-render) must not
  // retune the global Transport under another component's active playback.
  useEffect(() => {
    if (!parsed || !playing) return;
    Tone.getTransport().bpm.value = effectiveBpm;
  }, [effectiveBpm, parsed, playing]);

  // Progress animation, shared by a fresh play and a resume-from-pause.
  const ratioRef = useRef(1);
  const resumeTick = useCallback(() => {
    const tick = () => {
      const start = startedAtRef.current;
      const p0 = parsedRef.current;
      if (start == null || !p0) return;
      const elapsed = Tone.now() - start;
      const dur = p0.durationSec * ratioRef.current;
      let p = dur > 0 ? (elapsed % dur) / dur : 0;
      if (!loopRef.current && elapsed >= dur) p = 1;
      setProgress(p);
      tickRafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const stop = useCallback(() => {
    const t = Tone.getTransport();
    t.stop();
    t.cancel();
    scheduledIdsRef.current.forEach((id) => t.clear(id));
    scheduledIdsRef.current = [];
    if (tickRafRef.current != null) cancelAnimationFrame(tickRafRef.current);
    tickRafRef.current = null;
    startedAtRef.current = null;
    pausedAtRef.current = null;
    setPlaying(false);
    setProgress(0);
  }, []);

  /**
   * Real pause: hold the transport where it is and keep the schedule, so
   * Play resumes from the same bar. The button used to carry a pause glyph
   * and an aria-label of "Pause" while calling stop() and resetting to zero.
   */
  const pause = useCallback(() => {
    const t = Tone.getTransport();
    t.pause();
    if (tickRafRef.current != null) cancelAnimationFrame(tickRafRef.current);
    tickRafRef.current = null;
    if (startedAtRef.current != null) {
      pausedAtRef.current = Tone.now() - startedAtRef.current;
    }
    startedAtRef.current = null;
    setPlaying(false);
  }, []);

  const play = useCallback(async () => {
    if (!parsed) return;
    kickAudio();

    // Resuming a pause: the schedule is still intact, just let it run on.
    if (pausedAtRef.current != null && scheduledIdsRef.current.length) {
      const elapsed = pausedAtRef.current;
      pausedAtRef.current = null;
      Tone.getTransport().start();
      startedAtRef.current = Tone.now() - elapsed;
      setPlaying(true);
      resumeTick();
      return;
    }
    // Cancel anything else that was playing — global stop bus.
    // The bus listener above will set our own state to stopped, which is
    // fine because the next lines mark us as playing again.
    stopAllAudio();
    const piano = await getPiano();
    const t = Tone.getTransport();
    scheduledIdsRef.current = [];

    t.bpm.value = effectiveBpm;
    const ratio = fileBpm / effectiveBpm; // playback time = file time × ratio

    // schedule notes
    for (const n of parsed.notes) {
      const playTime = n.time * ratio;
      const dur = n.duration * ratio;
      const id = t.schedule((when) => {
        piano.triggerAttackRelease(n.pitchName, dur, when, n.velocity);
        onNote?.(n.pc, n.midi);
      }, playTime);
      scheduledIdsRef.current.push(id);
    }

    // Loop or stop on end
    const total = parsed.durationSec * ratio + 0.05;
    if (loop) {
      t.loop = true;
      t.loopStart = 0;
      t.loopEnd = total;
    } else {
      t.loop = false;
      const endId = t.schedule(() => {
        // microtask to avoid mutating Transport from inside a callback
        setTimeout(() => {
          stop();
          onEnd?.();
        }, 5);
      }, total);
      scheduledIdsRef.current.push(endId);
    }

    t.position = "0:0:0";
    t.start("+0.05");
    startedAtRef.current = Tone.now();
    setPlaying(true);

    ratioRef.current = ratio;
    resumeTick();
  }, [parsed, effectiveBpm, fileBpm, loop, onNote, onEnd, stop, resumeTick]);

  // Cleanup on unmount — but only stop the (global) Transport if WE were the
  // one playing; unmounting an idle player must not kill another component's
  // active rhythm/sequence playback.
  const playingRef = useRef(false);
  playingRef.current = playing;
  useEffect(() => {
    return () => {
      if (playingRef.current) stop();
    };
  }, [stop]);

  // If anything else starts playing, our audio has been silenced — reflect
  // that in our UI (button flips back to ▶, progress freezes, no fake state).
  useEffect(() => {
    return onAudioStop(() => {
      if (tickRafRef.current != null) cancelAnimationFrame(tickRafRef.current);
      tickRafRef.current = null;
      startedAtRef.current = null;
      pausedAtRef.current = null;
      scheduledIdsRef.current = [];
      setPlaying(false);
      setProgress(0);
    });
  }, []);

  // Restart automatically when speed or loop changes mid-play, so it keeps groove
  useEffect(() => {
    if (playing) {
      void play();
    } else if (pausedAtRef.current != null) {
      // Paused: the existing schedule is now at the wrong tempo, so drop it and
      // let the next Play rebuild rather than resuming into the old timing.
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speedPct, loop]);

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-950/40 via-zinc-900/40 to-amber-950/10 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ♪ MIDI playback{label ? ` · ${label}` : ""}
        </div>
        {parsed && (
          <div className="font-mono text-[11px] text-cyan-300">
            {effectiveBpm} bpm
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => (playing ? pause() : play())}
          disabled={loading || !!error || !parsed}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400 text-zinc-900 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="2" y="2" width="3" height="10"/><rect x="9" y="2" width="3" height="10"/></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2 L12 7 L3 12 Z"/></svg>
          )}
        </button>

        <button
          onClick={() => stop()}
          disabled={!playing && pausedAtRef.current == null}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Stop"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="2" width="8" height="8" rx="1"/></svg>
        </button>

        <label className="ml-2 flex select-none items-center gap-1.5 text-[12px] text-cyan-200">
          <input
            type="checkbox"
            checked={loop}
            onChange={(e) => setLoop(e.target.checked)}
            className="h-3.5 w-3.5 accent-amber-400"
          />
          Loop
        </label>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-cyan-300">Speed</span>
          <input
            type="range"
            min={50}
            max={150}
            step={5}
            value={speedPct}
            onChange={(e) => setSpeedPct(Number(e.target.value))}
            className="h-1 w-32 cursor-pointer accent-amber-400"
            aria-label="Playback speed"
          />
          <span className="w-10 text-right font-mono text-[11px] text-cyan-200">{speedPct}%</span>
        </div>
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-cyan-900/60">
        <div
          className="h-full bg-gradient-to-r from-cyan-400 to-amber-400 transition-[width] duration-100"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {loading && (
        <div className="mt-2 text-[12px] text-cyan-300/70">Loading…</div>
      )}
      {error && (
        <div className="mt-2 text-[12px] text-rose-300">⚠ {error}</div>
      )}
    </div>
  );
}

/**
 * Convenience wrapper: only renders the MidiPlayer if a source is set
 * (either via the manifest or via an admin upload). When nothing is set,
 * a one-line pointer to the video's loop and speed controls is shown instead
 * (an empty "Play along" section read as broken). Admin is where missing MIDI surfaces.
 */
export function LessonMidi({
  courseSlug,
  order,
  label,
  originalBpm,
  onNote,
}: {
  courseSlug: string;
  order: string;
  label?: string;
  originalBpm?: number;
  onNote?: (pc: number, midi: number) => void;
}) {
  const src = useLessonMidi(courseSlug, order);
  // No backing track has been made for this drill yet. Returning nothing left a
  // "Play along" heading over an empty space on 21 Piano Intermediate pages, so
  // point the student at the play-along that does exist: Jason's own video.
  if (!src) {
    return (
      <p className="my-2 rounded-xl border border-nsm-border bg-nsm-card/40 px-4 py-3 text-[14px] leading-relaxed text-nsm-dim">
        Play along with Jason in the lesson video at the top of the page — press{" "}
        <strong className="text-nsm-ink">Loop the last 10s</strong> on the bar you want and try it at{" "}
        <strong className="text-nsm-ink">0.75×</strong> before full speed.
      </p>
    );
  }
  return (
    <MidiPlayer src={src} label={label} originalBpm={originalBpm} onNote={onNote} />
  );
}

/**
 * Resolve the MIDI source for a given lesson key, checking
 *   (1) a localStorage admin override (uploaded via AdminPanel), then
 *   (2) the static manifest in `data/midi.ts`.
 *
 * Returns null if no MIDI is set for this lesson.
 */
export function useLessonMidi(courseSlug: string, order: string): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const key = `${courseSlug}/${order}`;

    // 1) localStorage override
    let overridden = false;
    try {
      const raw = localStorage.getItem(`efl-midi:${key}`);
      if (raw) {
        setSrc(raw);
        overridden = true;
      }
    } catch {
      /* ignore — privacy mode etc. */
    }

    // 2) manifest — only when there's no override to respect
    if (!overridden) {
      void import("@/data/midi").then((m) => {
        const entry = m.MIDI[key];
        setSrc(entry?.src ?? null);
      });
    }

    // Listen for admin uploads. This used to sit after an early `return`, so
    // once an override existed no further upload was ever picked up.
    const onStorage = (e: StorageEvent) => {
      if (e.key === `efl-midi:${key}`) setSrc(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [courseSlug, order]);
  return src;
}
